import { useState, useEffect, useCallback } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { logger } from '@azure/storage-blob';
import { Box, Button, Menu, MenuItem, Stack } from '@mui/material';

import { MasterIndexApi, MasterIndexApiPath } from '@/api';
import { AddressNoteApi } from '@/api/api.address.notes';
import { MasterNameApi } from '@/api/api.names.master';
import { MasterVehicleApi } from '@/api/api.vehicles.master';
import { IcArrowDown, IcShieldPerson } from '@/assets/images';
import { ConfirmationDialog, FlagChips, Page, PageHeader } from '@/components';
import { useSearchPersist } from '@/components/DataGrid/hooks/useSearchPersist';
import NavigationButtonGroup from '@/components/NavigationButtonGroup/NavigationButtonGroup';
import { IMasterNamePerson, IUser, Role } from '@/models';
import { MasterIndexUpdatedBadge } from '@/pages/MasterIndex/components/MasterIndexUpdatedBadge';
import { MergeToolPanel } from '@/pages/MasterIndex/components/MergeTool/MergeToolPanel';
import PersonPrintModal from '@/pages/MasterIndex/Persons/reports/PersonPrintModal';
import { usePersonPrint } from '@/pages/MasterIndex/Persons/reports/usePersonPrint';
import { MasterIndexTabRoutes } from '@/routes/RoutePaths';
import { isAdmin, isDispatcherRole, isTargetRole } from '@/services';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';

// Define the types for the MasterIndexDetailsPageProps here
export type MasterIndexType =
  | 'persons'
  | 'organizations'
  | 'vehicles'
  | 'address';
export type MasterIndexAction = 'new' | 'edit' | 'view';

// Define the context type
export type MasterIndexDetailsContext = {
  setCustomTitle: (title: string) => void;
  setLastUpdatedAt: (updatedAt?: string | Date | null) => void;
  onFlagsUpdate?: () => void;
  refreshTabCounts?: () => void;
};

type MasterIndexDetailsPageProps = {
  action: MasterIndexAction;
  masterIndexType: MasterIndexType;
};

interface TabCounts {
  relatedRecords: number;
  flags: number;
  notes: number;
}

const RELATED_RECORDS_DENIED_TOOLTIP = 'Access denied by admin.';

// Related records are withheld from holders of the per-section Master Index
// roles (First Responder MDT permissions); admins and dispatchers keep access
// even when they hold the role.
const isRelatedRecordsDenied = (
  user: IUser | null,
  userAgencyId: string | null,
  sectionRole: Role,
): boolean =>
  !!user &&
  !!userAgencyId &&
  !isAdmin(user, userAgencyId) &&
  !isDispatcherRole(user) &&
  isTargetRole(user, sectionRole);

// setup tabs for each master index type, return empty array if details page has no tabs
const getTabs = (
  type: MasterIndexType,
  action: MasterIndexAction,
  user: IUser | null,
  userAgencyId: string | null,
  counts?: TabCounts,
): {
  label: string;
  path: string;
  disabled?: boolean;
  count?: number;
  tooltip?: string;
}[] => {
  switch (type) {
    case 'persons':
      return [
        {
          label: 'Personal information',
          path: MasterIndexTabRoutes.Information,
        },
        isRelatedRecordsDenied(user, userAgencyId, Role.MASTER_INDEX_PERSONS)
          ? {
              label: 'Related records',
              path: MasterIndexTabRoutes.Related,
              disabled: true,
              tooltip: RELATED_RECORDS_DENIED_TOOLTIP,
            }
          : {
              label: 'Related records',
              path: MasterIndexTabRoutes.Related,
              disabled: action === 'new',
              count: counts?.relatedRecords,
            },
        {
          label: 'Flags',
          path: MasterIndexTabRoutes.Flags,
          disabled: action === 'new',
          count: counts?.flags,
        },
        {
          label: 'Notes',
          path: MasterIndexTabRoutes.Notes,
          disabled: action === 'new',
          count: counts?.notes,
        },
      ];
    case 'organizations':
      return [
        {
          label: 'Organization information',
          path: MasterIndexTabRoutes.Information,
        },
        {
          label: 'Related records',
          path: MasterIndexTabRoutes.Related,
          disabled: action === 'new',
          count: counts?.relatedRecords,
        },
        {
          label: 'Notes',
          path: MasterIndexTabRoutes.Notes,
          disabled: action === 'new',
          count: counts?.notes,
        },
      ];
    case 'vehicles':
      return [
        {
          label: 'Vehicle information',
          path: MasterIndexTabRoutes.Information,
        },
        isRelatedRecordsDenied(user, userAgencyId, Role.MASTER_INDEX_VEHICLES)
          ? {
              label: 'Related records',
              path: MasterIndexTabRoutes.Related,
              disabled: true,
              tooltip: RELATED_RECORDS_DENIED_TOOLTIP,
            }
          : {
              label: 'Related records',
              path: MasterIndexTabRoutes.Related,
              disabled: action === 'new',
              count: counts?.relatedRecords,
            },
        {
          label: 'Notes',
          path: MasterIndexTabRoutes.Notes,
          disabled: action === 'new',
          count: counts?.notes,
        },
      ];
    case 'address': {
      const commonTabs: {
        label: string;
        path: MasterIndexTabRoutes;
        disabled?: boolean;
        count?: number;
      }[] = [
        {
          label: 'Address information',
          path: MasterIndexTabRoutes.Information,
        },
      ];

      if (
        user &&
        userAgencyId &&
        !isRelatedRecordsDenied(user, userAgencyId, Role.MASTER_INDEX_ADDRESSES)
      ) {
        commonTabs.push({
          label: 'Related records',
          path: MasterIndexTabRoutes.Related,
          disabled: action === 'new',
          count: counts?.relatedRecords,
        });
      }

      commonTabs.push({
        label: 'Notes',
        path: MasterIndexTabRoutes.AddressNotes,
        count: counts?.notes,
      });

      return commonTabs;
    }
    default:
      return [];
  }
};
const getBackRoute = (type: MasterIndexType): string => {
  return `/master-index/${type}`;
};

const getTitle = (type: MasterIndexType, action: MasterIndexAction): string => {
  if (type === 'address') {
    if (action === 'new') return 'Add Address';
    if (action === 'view') return 'View Address';
    if (action === 'edit') return 'Edit Address';
    return '';
  }

  switch (action) {
    case 'new':
      return `New ${type.slice(0, -1)}`;
    case 'edit':
      return `Edit ${type.slice(0, -1)}`;
    case 'view':
      return `View ${type.slice(0, -1)}`;
    default:
      return '';
  }
};

export const MasterIndexDetailsPage = ({
  action,
  masterIndexType,
}: MasterIndexDetailsPageProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { searchQuery } = useSearchPersist();
  const { id, addressId, vehicleId } = useParams();
  const paths = pathname.split('/');
  const { updateToast } = useToastStore();
  const { user, userAgencyId } = useUserStore();

  // Add state for custom title
  const [customTitle, setCustomTitle] = useState<string | null>(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string | Date | null>(
    null,
  );
  const [personData, setPersonData] = useState<IMasterNamePerson | null>(null);
  const [refreshPersonData, setRefreshPersonData] = useState(0);
  const [actionsMenuAnchor, setActionsMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showMergeTool, setShowMergeTool] = useState(false);
  const [tabCounts, setTabCounts] = useState<TabCounts>({
    relatedRecords: 0,
    flags: 0,
    notes: 0,
  });

  // Fetch person data when viewing or editing a person
  useEffect(() => {
    const fetchPersonData = async () => {
      if (
        masterIndexType === 'persons' &&
        (action === 'view' || action === 'edit') &&
        id
      ) {
        const response = await MasterIndexApi.getMasterPerson(id);
        setPersonData(response.data);
        setLastUpdatedAt(response.data?.updatedAt);
      }
    };

    fetchPersonData();
  }, [masterIndexType, action, id, refreshPersonData]);

  useEffect(() => {
    setLastUpdatedAt(null);
  }, [masterIndexType, action, id, addressId, vehicleId]);

  useEffect(() => {
    const fetchTabCounts = async () => {
      if (action === 'new') return;

      const entityId =
        masterIndexType === 'address'
          ? addressId
          : masterIndexType === 'vehicles'
          ? vehicleId
          : id;
      if (!entityId) return;

      try {
        let counts: TabCounts = { relatedRecords: 0, flags: 0, notes: 0 };

        switch (masterIndexType) {
          case 'persons': {
            const [tabRes, recordCountsRes] = await Promise.all([
              MasterIndexApi.getPersonTabCounts(entityId),
              MasterIndexApi.getPersonRelatedRecordCounts(entityId),
            ]);
            const rc = recordCountsRes.data;
            counts = {
              ...tabRes.data,
              relatedRecords:
                rc.cfs +
                rc.arrests +
                rc.cases +
                rc.warrants +
                rc.tasks +
                rc.dir,
            };
            break;
          }
          case 'organizations': {
            const [relatedRes, notesRes] = await Promise.all([
              MasterIndexApi.getMasterRelatedRecords(
                MasterIndexApiPath.Organizations,
                entityId,
                { sort: JSON.stringify({ createdAt: -1 }) },
              ),
              MasterNameApi.getPaginatedMasterNameNotes(entityId, { limit: 1 }),
            ]);
            counts.relatedRecords =
              relatedRes.data?.totalDocs || relatedRes.data?.docs?.length || 0;
            counts.notes = notesRes.data?.totalDocs || 0;
            break;
          }
          case 'vehicles': {
            const [relatedRes, notesRes] = await Promise.all([
              MasterIndexApi.getMasterRelatedRecords(
                MasterIndexApiPath.Vehicles,
                entityId,
                { sort: JSON.stringify({ createdAt: -1 }) },
              ),
              MasterVehicleApi.getPaginatedMasterVehicleNotes(entityId, {
                limit: 1,
              }),
            ]);
            counts.relatedRecords =
              relatedRes.data?.totalDocs || relatedRes.data?.docs?.length || 0;
            counts.notes = notesRes.data?.totalDocs || 0;
            break;
          }
          case 'address': {
            const [relatedRes, notesRes] = await Promise.all([
              MasterIndexApi.getAddressRelatedRecords(entityId),
              AddressNoteApi.getPaginatedAddressNotes({
                filter: JSON.stringify({
                  items: [
                    { field: 'address', operator: 'equals', value: entityId },
                  ],
                }),
                limit: 1,
              }),
            ]);
            counts.relatedRecords = Array.isArray(relatedRes.data)
              ? relatedRes.data.length
              : relatedRes.data?.totalDocs || 0;
            counts.notes = notesRes.data?.totalDocs || 0;
            break;
          }
        }

        setTabCounts(counts);
      } catch (error) {
        logger.error('Failed to fetch tab counts:', error);
      }
    };

    fetchTabCounts();
  }, [masterIndexType, action, id, addressId, vehicleId, refreshPersonData]);

  const activeTab = paths[paths.length - 1];
  const tabs = getTabs(
    masterIndexType,
    action,
    user,
    userAgencyId,
    action !== 'new' ? tabCounts : undefined,
  );
  const title = customTitle || getTitle(masterIndexType, action);
  const backRoute = `${getBackRoute(masterIndexType)}${searchQuery}`;
  const openActionsMenu = Boolean(actionsMenuAnchor);
  const canDeleteMasterIndex = Boolean(
    user &&
      ((userAgencyId && isAdmin(user, userAgencyId)) ||
        isTargetRole(user, Role.ACCOUNT_ADMIN)),
  );

  const handleCancel = () => {
    navigate(backRoute);
  };

  const handleSave = () => {
    try {
      const form = document.getElementById(
        'master-index-form',
      ) as HTMLFormElement;
      if (form) {
        // Trigger form submission using form.submit()
        const submitEvent = new Event('submit', {
          bubbles: true,
          cancelable: true,
        });
        form.dispatchEvent(submitEvent);
      }
    } catch (error) {
      logger.error('Form submission error:', error);
      updateToast({
        open: true,
        message: 'Failed to save form',
        type: ConfirmTypes.ERROR,
      });
    }
  };

  const handleEdit = () => {
    let targetId = id;
    if (masterIndexType === 'address') {
      targetId = addressId;
    }
    if (masterIndexType === 'vehicles') {
      targetId = vehicleId;
    }

    navigate(`/master-index/${masterIndexType}/${targetId}/edit`);
  };

  const { printPersonId, print, closePrint } = usePersonPrint();

  const handleOpenActionsMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleCloseActionsMenu = () => {
    setActionsMenuAnchor(null);
  };

  const getCurrentEntityId = () => {
    if (masterIndexType === 'address') return addressId;
    if (masterIndexType === 'vehicles') return vehicleId;
    return id;
  };

  const getDeleteDialogContent = () => {
    switch (masterIndexType) {
      case 'persons':
        return {
          title: 'Confirm Delete Person Record',
          description:
            'Are you sure you want to permanently delete this person from the records? This action cannot be undone and will remove all related information from the system.',
          successMessage: 'Person deleted successfully',
        };
      case 'organizations':
        return {
          title: 'Confirm Delete Organization Record',
          description:
            'Are you sure you want to permanently delete this organization from the records? This action cannot be undone and will remove all related information from the system.',
          successMessage: 'Organization deleted successfully',
        };
      case 'vehicles':
        return {
          title: 'Delete Vehicle?',
          description: 'You are about to delete this vehicle, are you sure?',
          successMessage: 'Vehicle deleted successfully',
        };
      case 'address':
      default:
        return {
          title: 'Confirm Delete Address Record',
          description:
            'Are you sure you want to permanently delete this address from the records? This action cannot be undone and will remove all related information from the system.',
          successMessage: 'Address deleted successfully',
        };
    }
  };

  const handleDelete = async () => {
    const entityId = getCurrentEntityId();
    if (!entityId) return;

    const { successMessage } = getDeleteDialogContent();

    try {
      switch (masterIndexType) {
        case 'persons':
          await MasterIndexApi.deleteMasterPerson(entityId);
          break;
        case 'organizations':
          await MasterIndexApi.deleteMasterOrganization(entityId);
          break;
        case 'vehicles':
          await MasterIndexApi.deleteMasterVehicle(entityId);
          break;
        case 'address':
          await MasterIndexApi.deleteMasterAddress(entityId);
          break;
      }

      updateToast({
        open: true,
        type: ConfirmTypes.SUCCESS,
        message: successMessage,
      });
      navigate(backRoute, { replace: true });
    } catch (err: any) {
      updateToast({
        open: true,
        type: ConfirmTypes.ERROR,
        message: err?.message || 'Failed to delete record',
      });
    }
  };

  const handleRefreshPersonData = useCallback(() => {
    setRefreshPersonData((prev) => prev + 1);
  }, []);

  const renderActionButtons = () => {
    if (action === 'view' && activeTab === 'information') {
      return (
        <>
          <Button
            variant="contained"
            color="error"
            onClick={handleOpenActionsMenu}
            endIcon={<IcArrowDown />}
          >
            Actions
          </Button>
          <Menu
            id="master-index-actions-menu"
            anchorEl={actionsMenuAnchor}
            open={openActionsMenu}
            onClose={handleCloseActionsMenu}
            MenuListProps={{
              'aria-labelledby': 'master-index-actions-menu',
            }}
          >
            <MenuItem
              onClick={() => {
                handleEdit();
                handleCloseActionsMenu();
              }}
            >
              Edit
            </MenuItem>
            {canDeleteMasterIndex && (
              <MenuItem
                onClick={() => {
                  setShowMergeTool(true);
                  handleCloseActionsMenu();
                }}
              >
                Merge
              </MenuItem>
            )}
            {masterIndexType === 'persons' && (
              <MenuItem
                onClick={() => {
                  if (id) print(id);
                  handleCloseActionsMenu();
                }}
              >
                Print
              </MenuItem>
            )}
            {canDeleteMasterIndex && (
              <MenuItem
                onClick={() => {
                  setShowDeleteConfirm(true);
                  handleCloseActionsMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                Delete
              </MenuItem>
            )}
          </Menu>
        </>
      );
    }

    if (action === 'edit' && activeTab === 'information') {
      return (
        <Stack direction="row" spacing={2}>
          <Button variant="contained" color="inherit" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      );
    }

    if (action === 'new' && activeTab === 'information') {
      return (
        <Stack direction="row" spacing={2}>
          <Button variant="contained" color="inherit" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleSave}>
            Save
          </Button>
        </Stack>
      );
    }

    if (activeTab !== 'information') {
      return;
    }

    return (
      <Stack direction="row" spacing={2}>
        <Button variant="contained" color="inherit" onClick={handleCancel}>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={handleSave}>
          Save
        </Button>
      </Stack>
    );
  };

  const renderTitle = () => {
    const titleRowSpacing = 1;
    const showUpdatedAt = action !== 'new';
    const showEmployeeBadge =
      masterIndexType === 'persons' &&
      (action === 'view' || action === 'edit') &&
      personData?.isEmployee;

    if (
      masterIndexType === 'persons' &&
      (action === 'view' || action === 'edit') &&
      personData?.flags &&
      personData.flags.length > 0
    ) {
      return (
        <Stack direction="row" alignItems="center" spacing={titleRowSpacing}>
          <span>{title}</span>
          {showEmployeeBadge && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 1,
                backgroundColor: 'error.main',
              }}
            >
              <IcShieldPerson
                style={{ width: 18, height: 18, color: '#fff' }}
              />
            </Box>
          )}
          <FlagChips
            flags={personData.flags.map((f) => f.flag)}
            size="small"
            animateImportant
          />
          {showUpdatedAt && (
            <MasterIndexUpdatedBadge updatedAt={lastUpdatedAt} />
          )}
        </Stack>
      );
    }

    if (showUpdatedAt) {
      return (
        <Stack direction="row" alignItems="center" spacing={titleRowSpacing}>
          <span>{title}</span>
          {showEmployeeBadge && (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 1,
                backgroundColor: 'error.main',
              }}
            >
              <IcShieldPerson
                style={{ width: 18, height: 18, color: '#fff' }}
              />
            </Box>
          )}
          <MasterIndexUpdatedBadge updatedAt={lastUpdatedAt} />
        </Stack>
      );
    }

    return <span>{title}</span>;
  };

  return (
    <Page title={title}>
      <PageHeader
        title={renderTitle()}
        backRoute={backRoute}
        action={renderActionButtons()}
      />
      {tabs && <NavigationButtonGroup tabs={tabs} activeTab={activeTab} />}
      <Outlet
        context={{
          setCustomTitle,
          setLastUpdatedAt,
          onFlagsUpdate: handleRefreshPersonData,
          refreshTabCounts: handleRefreshPersonData,
        }}
      />
      <ConfirmationDialog
        title={getDeleteDialogContent().title}
        description={getDeleteDialogContent().description}
        confirmText="Delete"
        open={showDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
        }}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          handleDelete();
        }}
      />
      <PersonPrintModal personId={printPersonId} onClose={closePrint} />
      <MergeToolPanel
        open={showMergeTool}
        onClose={() => setShowMergeTool(false)}
        masterIndexType={masterIndexType}
        currentEntityId={getCurrentEntityId()}
        onMergeComplete={() => navigate(backRoute, { replace: true })}
      />
    </Page>
  );
};
